import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { Link } from 'react-router-dom';
import SALayout from './SALayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Building2, Search, Users, ChevronRight, Loader2,
  Pause, Play, Trash2, RotateCcw, Calendar, Sparkles, X,
} from 'lucide-react';
import { toast } from 'sonner';

function PlanBadge({ plan }) {
  const styles = { active: 'bg-emerald-100 text-emerald-700', trial: 'bg-amber-100 text-amber-700', business: 'bg-emerald-100 text-emerald-700', expired: 'bg-red-100 text-red-700', cancelled: 'bg-slate-200 text-slate-600', free: 'bg-slate-100 text-slate-500' };
  // Normalize the 'active' plan label to "Business" for SA readability —
  // org-level subscriptions use 'active' for paid Business but the SA UI
  // historically labels it as Business.
  const label = plan === 'active' ? 'Business' : plan;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[plan] || styles.free}`}>{label}</span>;
}

export default function SAOrganizations() {
  const { apiFetch } = useSuperAdmin();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [extendDays, setExtendDays] = useState(7);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (planFilter) params.set('plan', planFilter);
      if (includeDeleted) params.set('include_deleted', 'true');
      const res = await apiFetch(`/organizations?${params}`);
      if (res.ok) setOrgs(await res.json());
    } catch {} finally { setLoading(false); }
  }, [apiFetch, search, planFilter, includeDeleted]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  // Reset selection any time the visible orgs list changes (avoid orphan IDs).
  useEffect(() => { setSelected(new Set()); }, [orgs]);

  const allChecked = orgs.length > 0 && selected.size === orgs.length;
  const partialChecked = selected.size > 0 && selected.size < orgs.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(orgs.map(o => o.id)));
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const runBulk = async (action, payload = {}, confirmText = null) => {
    if (selected.size === 0) return;
    if (confirmText && !window.confirm(confirmText)) return;
    setBulkBusy(true);
    try {
      const res = await apiFetch('/organizations/bulk', {
        method: 'POST',
        body: JSON.stringify({ org_ids: Array.from(selected), action, payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Bulk ${action} failed`);
        return;
      }
      toast.success(`${action.replace('_', ' ')} applied to ${data.modified || selected.size} org(s)`);
      setSelected(new Set());
      fetchOrgs();
    } catch (err) {
      toast.error('Bulk action failed');
    } finally { setBulkBusy(false); }
  };

  const plans = useMemo(() => ['', 'trial', 'active', 'business', 'expired', 'cancelled', 'free'], []);

  return (
    <SALayout>
      <div className="space-y-4" data-testid="sa-organizations">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-slate-900">Organizations ({orgs.length})</h1>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)} data-testid="sa-org-include-deleted" />
              Show deleted
            </label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search org..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 pl-8 text-xs rounded-lg w-48"
                data-testid="sa-org-search"
              />
            </div>
            <select
              value={planFilter}
              onChange={e => setPlanFilter(e.target.value)}
              className="h-8 px-2 text-xs rounded-lg border border-slate-200 bg-white text-slate-700"
              data-testid="sa-org-plan-filter"
            >
              <option value="">All Plans</option>
              {plans.filter(Boolean).map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
        </div>

        {/* BULK ACTIONS TOOLBAR — only visible when ≥1 org is selected */}
        {selected.size > 0 && (
          <div className="sticky top-0 z-20 bg-slate-900 text-white rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 shadow-lg" data-testid="sa-bulk-toolbar">
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())} className="text-slate-300 hover:text-white" data-testid="sa-bulk-clear"><X size={14} /></button>
              <span className="text-[12px] font-semibold tabular-nums">{selected.size} selected</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-7 text-[11px] text-white hover:bg-white/10" onClick={() => runBulk('suspend', {}, `Suspend ${selected.size} org(s)? Members will be locked out until reactivated.`)} data-testid="sa-bulk-suspend">
                <Pause size={11} className="mr-1" /> Suspend
              </Button>
              <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-7 text-[11px] text-white hover:bg-white/10" onClick={() => runBulk('reactivate')} data-testid="sa-bulk-reactivate">
                <Play size={11} className="mr-1" /> Reactivate
              </Button>
              <div className="h-5 w-px bg-white/20 mx-1" />
              <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-7 text-[11px] text-white hover:bg-white/10" onClick={() => runBulk('set_plan', { plan: 'business' }, `Promote ${selected.size} org(s) to Business plan with a 1-year period?`)} data-testid="sa-bulk-set-business">
                <Sparkles size={11} className="mr-1" /> Set Business
              </Button>
              <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-7 text-[11px] text-white hover:bg-white/10" onClick={() => runBulk('set_plan', { plan: 'free' }, `Downgrade ${selected.size} org(s) to Free plan?`)} data-testid="sa-bulk-set-free">
                Set Free
              </Button>
              <div className="h-5 w-px bg-white/20 mx-1" />
              <div className="flex items-center gap-1 bg-white/10 rounded-md pl-2 pr-1">
                <Calendar size={11} />
                <input type="number" min={1} max={365} value={extendDays} onChange={e => setExtendDays(Number(e.target.value) || 7)} className="w-12 h-6 bg-transparent text-[11px] outline-none" data-testid="sa-bulk-extend-days" />
                <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-6 text-[11px] text-white hover:bg-white/20 px-2" onClick={() => runBulk('extend_trial', { days: extendDays })} data-testid="sa-bulk-extend-trial">
                  +{extendDays}d trial
                </Button>
              </div>
              <div className="h-5 w-px bg-white/20 mx-1" />
              <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-7 text-[11px] text-red-300 hover:bg-red-500/20" onClick={() => runBulk('soft_delete', {}, `Soft-delete ${selected.size} org(s)? They'll be hidden from listings but can be restored within 30 days.`)} data-testid="sa-bulk-delete">
                <Trash2 size={11} className="mr-1" /> Soft delete
              </Button>
              {includeDeleted && (
                <Button size="sm" variant="ghost" disabled={bulkBusy} className="h-7 text-[11px] text-emerald-300 hover:bg-emerald-500/20" onClick={() => runBulk('restore')} data-testid="sa-bulk-restore">
                  <RotateCcw size={11} className="mr-1" /> Restore
                </Button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={el => { if (el) el.indeterminate = partialChecked; }}
                      onChange={toggleAll}
                      data-testid="sa-org-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Organization</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Plan</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-slate-500">Members</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden md:table-cell">Renewal / Trial Ends</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden md:table-cell">Price/User</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">Total Spend</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden lg:table-cell">Promo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => {
                  const checked = selected.has(org.id);
                  return (
                    <tr key={org.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${org.is_deleted ? 'opacity-50 bg-red-50/30' : ''} ${checked ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={checked} onChange={() => toggle(org.id)} data-testid={`sa-org-row-cb-${org.id}`} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-xs truncate max-w-[180px]">
                              {org.name}
                              {org.is_deleted && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-red-500 font-bold">deleted</span>}
                              {org.is_suspended && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-amber-600 font-bold">suspended</span>}
                            </p>
                            <p className="text-[10px] text-slate-400">{new Date(org.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><PlanBadge plan={org.plan} /></td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-slate-600 flex items-center justify-center gap-1">
                          <Users className="w-3 h-3" /> {org.member_count}/{org.seat_limit}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 hidden md:table-cell" data-testid={`sa-org-renewal-${org.id}`}>
                        {(() => {
                          // Show renewal date for paid subs, trial end for trials.
                          // Flag expired paid subs in red so SA can triage them at a glance.
                          // Cancelled subs show in amber with a "Cancels" tag.
                          const end = org.current_period_end || org.trial_ends_at;
                          if (!end) return <span className="text-slate-300">-</span>;
                          const dt = new Date(end);
                          const daysLeft = Math.ceil((dt - new Date()) / 86400000);
                          const isExpired = daysLeft < 0;
                          const soon = daysLeft >= 0 && daysLeft <= 7;
                          const cancelled = !!org.cancel_at_period_end;
                          const dateTxt = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
                          if (cancelled) {
                            return (
                              <span className="text-amber-700 font-medium">
                                {dateTxt}
                                <span className="ml-1 text-[9px] uppercase tracking-wider font-bold">Cancels</span>
                              </span>
                            );
                          }
                          return (
                            <span className={isExpired ? 'text-red-600 font-semibold' : soon ? 'text-amber-700 font-medium' : 'text-slate-500'}>
                              {dateTxt}
                              {isExpired && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold">Expired</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 hidden md:table-cell">
                        {Number.isFinite(Number(org.effective_price)) ? `₹${org.effective_price}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right" data-testid={`sa-org-spend-${org.id}`}>
                        {org.total_spend > 0 ? (
                          <span className="text-xs font-semibold text-green-700">₹{org.total_spend.toLocaleString('en-IN')}</span>
                        ) : (
                          <span className="text-xs text-slate-300">₹0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs hidden lg:table-cell">
                        {org.promo_code ? <span className="text-purple-600 font-medium">{org.promo_code}</span> : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`w-2 h-2 rounded-full inline-block ${org.is_deleted ? 'bg-red-500' : org.is_suspended ? 'bg-amber-500' : org.is_active ? 'bg-emerald-500' : 'bg-red-400'}`} />
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/superadmin/organizations/${org.id}`} data-testid={`sa-org-detail-${org.id}`}>
                          <ChevronRight className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
                {orgs.length === 0 && (
                  <tr><td colSpan="10" className="px-4 py-12 text-center text-sm text-slate-400">No organizations found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SALayout>
  );
}
